/**
 * **元動画の要素**（素材。Issue #643 で `/duel-editor` から切り出した）。絵は duelEditor が canvas に描くので、
 * この要素は時計・音声・盤の紙のテクスチャのためだけに DOM に置く。
 *
 * - **ツリーの位置を動かさない** —— 動かすと要素が作り直されて再生位置が頭に戻る
 * - 完全に隠す（`display: none` / 画面外）と再生が間引かれる環境があるので、1px でほぼ透明にして置く
 * - **`crossOrigin` は必須**（Issue #523）—— 汚れた video は WebGL のテクスチャにできない
 * - 読めなければ理由を調べて `onError` に渡す（署名切れと CORS は `MediaError` では見分けられない）
 */

/** `<video>` の失敗の理由（`MediaError` は数字しか持たない） */
const MEDIA_ERROR = {
  1: '読み込みを中断した（MEDIA_ERR_ABORTED）',
  2: 'ネットワーク（MEDIA_ERR_NETWORK）。**CORS で弾かれた可能性**',
  3: 'デコードできない（MEDIA_ERR_DECODE）',
  4: 'この形式・この URL は再生できない（MEDIA_ERR_SRC_NOT_SUPPORTED）。**署名の期限切れか CORS**',
};
export const mediaErrorText = (err) => (err ? `${MEDIA_ERROR[err.code] ?? `code ${err.code}`}${err.message ? ` / ${err.message}` : ''}` : '理由不明');

/** `crossOrigin` 無しの `<video>` なら読めるか（読めれば URL は有効で CORS だけの問題） */
function plainVideoLoads(url) {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    const done = (ok) => { v.removeAttribute('src'); v.load?.(); resolve(ok); };
    v.addEventListener('loadedmetadata', () => done(true), { once: true });
    v.addEventListener('error', () => done(false), { once: true });
    v.src = url;
  });
}

export async function probeUrl(url) {
  const where = (() => { try { return new URL(url).host; } catch { return '不明'; } })();
  const plain = await plainVideoLoads(url);
  if (!plain) return `${where} の URL そのものが読めない（**署名切れか権限**。CORS の話ではない）`;
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    return res.ok
      ? `${where} は CORS 付きでも HTTP ${res.status} を返す（**取れている**。再生できない理由は別）`
      : `${where} が HTTP ${res.status}`;
  } catch (e) {
    return `${where} は **crossOrigin 無しなら読める** が CORS では弾かれる（${e?.name ?? ''} ${e?.message ?? e}）`
      + ' —— ブラウザに CORS 無しの応答がキャッシュされている可能性（プライベートウィンドウで試す）';
  }
}

/**
 * @param videoRef `useDuelStage().attachVideo`
 * @param onSize 寸法が分かったら（`{ w, h }`）
 * @param onError 読めなかった理由（文字列。分かったら）。読めたら null
 */
export default function StageVideo({ src, videoRef, onSize, onError, preload = 'metadata' }) {
  return (
    <video
      ref={videoRef}
      src={src ?? undefined}
      crossOrigin="anonymous"
      muted
      playsInline
      preload={preload}
      style={{ position: 'absolute', left: 0, top: 0, width: 1, height: 1, opacity: 0.01, pointerEvents: 'none' }}
      onLoadedMetadata={(e) => {
        onError?.(null);
        onSize?.({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight });
      }}
      onError={(e) => {
        const why = mediaErrorText(e.currentTarget.error);
        const url = e.currentTarget.currentSrc || e.currentTarget.src;
        onError?.(why);
        if (url) probeUrl(url).then((more) => onError?.(`${why}\n${more}`));
      }}
    />
  );
}
